import React from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import logo from "../../../assets/svg/auth/logo.svg";
import { Button } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import type { BootstrapStep } from "../types/auth.types";

/**
 * What the user looks at between the session going active and the app being
 * safe to render - while whoami and the workspace call are in flight.
 *
 * It stands in for the whole signed-in shell rather than sitting inside it, so
 * the dashboard is never painted against data the backend has not answered for
 * yet. That is the difference from fetching on arrival: there is no first frame
 * of empty cards to flash past.
 */

/** Where each call leaves the bar. None reaches 100%: the screen is replaced by
 *  the app the moment the last one lands, and a bar that completed before the
 *  swap would read as "done, but still waiting". The middle step reads as a
 *  check because that is what it usually is - most sessions owe nothing, and
 *  the read that says so is all that happens. */
const STEPS: Record<BootstrapStep, { label: string; percent: number }> = {
  account: { label: "Checking your account…", percent: 35 },
  consent: { label: "Checking your agreements…", percent: 60 },
  workspace: { label: "Preparing your workspace…", percent: 85 },
};

/**
 * Fixed to the top of the viewport, above everything. The width transition is
 * what carries the eye between the two steps; the pulse keeps it alive while a
 * single slow call holds it at one value.
 */
const TopProgressBar: React.FC<{ percent: number; label: string }> = ({
  percent,
  label,
}) => (
  <div className="fixed inset-x-0 top-0 z-50 h-[3px] bg-brand-soft">
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-valuetext={label}
      className="h-full bg-brand animate-pulse transition-[width] duration-700 ease-out"
      style={{ width: `${percent}%` }}
    />
  </div>
);

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-white flex flex-col px-5 py-6 sm:px-12 sm:py-10">
    <header>
      <img src={logo} alt="Naurist" className="h-6 sm:h-8 w-auto" />
    </header>
    <main className="flex-1 flex items-center justify-center py-10">
      <div className="w-full max-w-[420px] text-center">{children}</div>
    </main>
  </div>
);

export const WorkspaceLoading: React.FC<{ step: BootstrapStep }> = ({
  step,
}) => {
  const { label, percent } = STEPS[step];

  return (
    <>
      <TopProgressBar percent={percent} label={label} />
      <Shell>
        <h1 className="text-[22px] 3xl:text-[24px] font-medium text-ink">
          Loading your workspace
        </h1>
        {/* Announced politely: the heading is what matters, the step is detail
            that changes under it. */}
        <p
          aria-live="polite"
          className="mt-2 text-[14px] 3xl:text-[15px] text-ink-muted"
        >
          {label}
        </p>
      </Shell>
    </>
  );
};

/**
 * The dead end the loading screen turns into when whoami cannot be reached.
 *
 * Gating the app on a request means owning the case where it fails: without a
 * way forward the user would sit on the progress bar for good. Logging out is
 * the second way out, for a session the backend will not accept at all.
 */
export const WorkspaceLoadFailed: React.FC<{
  message: string;
  onRetry: () => void;
}> = ({ message, onRetry }) => {
  const { signOut } = useClerk();
  const navigate = useNavigate();

  return (
    <Shell>
      <h1 className="text-[22px] 3xl:text-[24px] font-medium text-ink">
        We couldn&rsquo;t load your workspace
      </h1>
      <p className="mt-2 text-[14px] 3xl:text-[15px] text-ink-muted">
        {message}
      </p>

      <div className="mt-8 flex flex-col items-center gap-3">
        <Button variant="primary" size="lg" fullWidth onClick={onRetry}>
          Try again
        </Button>
        <Button
          variant="ghost"
          size="md"
          onClick={() => signOut(() => navigate(ROUTES.login, { replace: true }))}
        >
          Log out
        </Button>
      </div>
    </Shell>
  );
};
